/**
 * What: Draws the read-only Worker-sent blocks: the `product` card and the `note` line. (Information cards are drawn as
 *   `group` blocks — `card`/`illustration-card` styles — so there is one renderer for every box.)
 * Transport / data-flow: Pure presentation over the payload. Nothing here is a choice or a field: no value, no edit, no
 *   words of its own. `SectionBlocks` hands the block in.
 */
import type { BuilderBlock } from '@testfeed/shopify-contracts'
import type { ReactNode } from 'react'
import { Banner } from './Banner'
import { BlockHeading } from './SectionFrame'

/**
 * A warning/status banner: the Worker's tone and optional heading, one line of text, and an optional action the caller has
 * already rendered (so this stays pure presentation with no edit logic). This is the look the old builder used for its
 * warnings — a true banner, not the plain tinted `note` line. It draws through the shared `Banner`, the builder's one
 * `s-banner` owner, so the `no-own-section-frame-in-builder-ui` ratchet stays intact.
 */
export function BannerBlock({
  block,
  action,
}: {
  block: Extract<BuilderBlock, { kind: 'banner' }>
  action: ReactNode
}) {
  return (
    <Banner tone={block.tone} heading={block.heading ?? undefined}>
      <s-stack direction="block" gap="small-200">
        <s-text>{block.text}</s-text>
        {action}
      </s-stack>
    </Banner>
  )
}

export function ProductBlock({ block }: { block: Extract<BuilderBlock, { kind: 'product' }> }) {
  return (
    <s-stack direction="inline" gap="base" alignItems="start">
      {block.image ? (
        <s-box inlineSize="64px" blockSize="64px" borderRadius="base" overflow="hidden">
          <s-image src={block.image.src} alt={block.image.alt} inlineSize="fill" objectFit="cover" />
        </s-box>
      ) : null}
      <s-stack direction="block" gap="small-200">
        <s-stack direction="inline" gap="small-200" alignItems="center">
          <s-text type="strong">{block.title}</s-text>
          {block.badge ? <s-badge tone={block.badge.tone}>{block.badge.text}</s-badge> : null}
        </s-stack>
        <s-text color="subdued">{block.detail}</s-text>
      </s-stack>
    </s-stack>
  )
}

export function NoteBlock({ block }: { block: Extract<BuilderBlock, { kind: 'note' }> }) {
  // A working line: a spinner beside the text (the original suggestions "Preparing test variations…" look).
  if (block.busy)
    return (
      <s-box padding="small-200">
        <s-stack direction="inline" gap="small-200" alignItems="center">
          <s-spinner accessibilityLabel={block.text} />
          <s-text color={block.tone === 'subdued' ? 'subdued' : undefined}>{block.text}</s-text>
        </s-stack>
      </s-box>
    )
  // `heading` draws a true heading (heavier than `strong`) through SectionFrame's BlockHeading, the single s-heading
  // owner, so the ratchet stays intact; `strong` stays bold text (the weight BuilderBox gives its titles).
  if (block.emphasis === 'heading') return <BlockHeading>{block.text}</BlockHeading>
  if (block.emphasis === 'strong') return <s-text type="strong">{block.text}</s-text>
  // `base` is plain default-colour text; `subdued` is grey; `critical`/`warning` are the red/amber tones.
  if (block.tone === 'base') return <s-text>{block.text}</s-text>
  if (block.tone === 'subdued') return <s-text color="subdued">{block.text}</s-text>
  return <s-text tone={block.tone}>{block.text}</s-text>
}
